'use client'

import React from 'react'
import {
  DollarSign,
  Zap,
  Mail,
  Brain,
  BarChart3,
  Send,
  User,
  Bot,
  Target,
  Bell,
  FileText,
  ArrowDown,
  CheckCircle,
  XCircle,
  Timer,
  MessageSquare,
} from 'lucide-react'
import BlogCTACard from '@/components/blog/BlogCTACard'

/**
 * AI Lead Qualification Diagrams
 * Visual components for the AI lead qualification blog post
 * Brand colors: Primary #008dcc, Accent #FDC304, Pink #F90B8A, Purple #8b5cf6
 */

/**
 * Traditional vs AI Flow Comparison
 */
export function LeadFlowComparisonDiagram() {
  const traditionalSteps = [
    {
      label: 'Form Submitted',
      sublabel: '15+ fields',
      icon: FileText,
      color: '#6b7280',
    },
    {
      label: 'Generic Auto-Reply',
      sublabel: '"Thanks, we\'ll be in touch"',
      icon: Mail,
      color: '#6b7280',
    },
    {
      label: 'Manual Review',
      sublabel: '24-72 hours later',
      icon: User,
      color: '#f97316',
    },
    {
      label: 'CRM Data Entry',
      sublabel: 'Manual scoring',
      icon: BarChart3,
      color: '#6b7280',
    },
    {
      label: 'First Real Response',
      sublabel: '1-7 days average',
      icon: Send,
      color: '#ef4444',
    },
  ]

  const aiSteps = [
    {
      label: 'Form Submitted',
      sublabel: '5 fields',
      icon: FileText,
      color: '#008dcc',
    },
    {
      label: 'Instant AI Qualification',
      sublabel: 'Score 0-100 in <1 second',
      icon: Brain,
      color: '#8b5cf6',
    },
    {
      label: 'Personalized AI Response',
      sublabel: 'References their exact message',
      icon: Bot,
      color: '#008dcc',
    },
    {
      label: 'Smart Routing',
      sublabel: 'High-value: priority alert',
      icon: Target,
      color: '#FDC304',
    },
    {
      label: 'Lead Sees Response',
      sublabel: 'Instant, on-screen',
      icon: CheckCircle,
      color: '#22c55e',
    },
  ]

  return (
    <div
      data-blog-layout='narrow'
      className='my-8 p-6 md:p-8 bg-muted/50 rounded-2xl border border-border/60'
    >
      <div className='text-center mb-6'>
        <h3 className='text-xl md:text-2xl font-bold text-foreground mb-2'>
          TRADITIONAL vs AI-FIRST LEAD FLOW
        </h3>
        <p className='text-sm text-muted-foreground'>
          Same goal, fundamentally different experience
        </p>
      </div>

      <div className='grid md:grid-cols-2 gap-6'>
        {/* Traditional Flow */}
        <div>
          <div className='text-center mb-4'>
            <span className='inline-flex items-center gap-2 px-3 py-1 rounded-full bg-red-500/10 border border-red-500/30 text-red-600 text-xs font-semibold uppercase tracking-wider'>
              <XCircle className='w-3 h-3' />
              Traditional (HubSpot)
            </span>
          </div>
          <div className='flex flex-col gap-2'>
            {traditionalSteps.map((step, i) => (
              <React.Fragment key={step.label}>
                <div className='p-3 rounded-lg bg-background border border-border/60'>
                  <div className='flex items-center gap-3'>
                    <div
                      className='w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0'
                      style={{ backgroundColor: `${step.color}15` }}
                    >
                      <step.icon
                        className='w-4 h-4'
                        style={{ color: step.color }}
                      />
                    </div>
                    <div>
                      <p className='text-sm font-semibold text-foreground leading-tight'>
                        {step.label}
                      </p>
                      <p className='text-xs text-muted-foreground'>
                        {step.sublabel}
                      </p>
                    </div>
                  </div>
                </div>
                {i < traditionalSteps.length - 1 && (
                  <div className='flex justify-center'>
                    <ArrowDown className='w-4 h-4 text-muted-foreground' />
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
          <div className='mt-3 text-center'>
            <span className='text-xs font-semibold text-red-500'>
              <Timer className='w-3 h-3 inline mr-1' />
              Total time to meaningful response: 1-7 days
            </span>
          </div>
        </div>

        {/* AI Flow */}
        <div>
          <div className='text-center mb-4'>
            <span className='inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#008dcc]/10 border border-[#008dcc]/30 text-[#008dcc] text-xs font-semibold uppercase tracking-wider'>
              <Zap className='w-3 h-3' />
              AI-First (Custom Built)
            </span>
          </div>
          <div className='flex flex-col gap-2'>
            {aiSteps.map((step, i) => (
              <React.Fragment key={step.label}>
                <div className='p-3 rounded-lg bg-background border border-border/60'>
                  <div className='flex items-center gap-3'>
                    <div
                      className='w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0'
                      style={{ backgroundColor: `${step.color}15` }}
                    >
                      <step.icon
                        className='w-4 h-4'
                        style={{ color: step.color }}
                      />
                    </div>
                    <div>
                      <p className='text-sm font-semibold text-foreground leading-tight'>
                        {step.label}
                      </p>
                      <p className='text-xs text-muted-foreground'>
                        {step.sublabel}
                      </p>
                    </div>
                  </div>
                </div>
                {i < aiSteps.length - 1 && (
                  <div className='flex justify-center'>
                    <ArrowDown className='w-4 h-4 text-[#008dcc]/40' />
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
          <div className='mt-3 text-center'>
            <span className='text-xs font-semibold text-[#008dcc]'>
              <Zap className='w-3 h-3 inline mr-1' />
              Total time to meaningful response: &lt;3 seconds
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}

/**
 * Cost Comparison Diagram
 */
export function LeadCostComparisonDiagram() {
  const tiers = [
    {
      name: 'HubSpot Starter',
      monthly: '$20/seat',
      annual: '$108/yr per seat',
      note: 'No lead scoring or automation',
      color: '#f97316',
      barWidth: '10%',
    },
    {
      name: 'HubSpot Professional',
      monthly: '$890',
      annual: '$10,680/yr',
      note: '+ $3,000 onboarding fee, 3 seats included',
      color: '#ef4444',
      barWidth: '60%',
    },
    {
      name: 'HubSpot Enterprise',
      monthly: '$3,600',
      annual: '$43,200/yr',
      note: '5 seats included, extra seats $75/mo each',
      color: '#dc2626',
      barWidth: '100%',
    },
    {
      name: 'Custom AI (this build)',
      monthly: '$0.29 per 1K leads',
      annual: '~$3.50/yr at 1K leads/mo',
      note: 'OpenAI API + Vercel + Resend (free tier)',
      color: '#008dcc',
      barWidth: '1%',
    },
  ]

  return (
    <div
      data-blog-layout='narrow'
      className='my-8 p-6 md:p-8 bg-muted/50 rounded-2xl border border-border/60'
    >
      <div className='text-center mb-6'>
        <h3 className='text-xl md:text-2xl font-bold text-foreground mb-2'>
          MONTHLY COST COMPARISON
        </h3>
        <p className='text-sm text-muted-foreground'>
          What you pay for lead qualification and auto-response
        </p>
      </div>

      <div className='space-y-4'>
        {tiers.map(tier => (
          <div
            key={tier.name}
            className='p-4 rounded-lg bg-background border border-border/60'
          >
            <div className='flex items-center justify-between mb-2'>
              <span className='text-sm font-semibold text-foreground'>
                {tier.name}
              </span>
              <span className='text-sm font-bold' style={{ color: tier.color }}>
                {tier.monthly}/mo
              </span>
            </div>
            <div className='w-full h-3 bg-muted rounded-full overflow-hidden mb-2'>
              <div
                className='h-full rounded-full transition-all duration-500'
                style={{
                  width: tier.barWidth,
                  backgroundColor: tier.color,
                  minWidth: '4px',
                }}
              />
            </div>
            <p className='text-xs text-muted-foreground'>{tier.note}</p>
          </div>
        ))}
      </div>

      <div className='mt-6 p-4 rounded-lg bg-[#008dcc]/5 border border-[#008dcc]/20'>
        <div className='flex items-start gap-3'>
          <DollarSign className='w-5 h-5 text-[#008dcc] flex-shrink-0 mt-0.5' />
          <div>
            <p className='text-sm font-semibold text-foreground'>
              The math at 1,000 leads/month:
            </p>
            <p className='text-xs text-muted-foreground mt-1'>
              HubSpot Pro: $890 = <strong>$0.89/lead</strong> in platform fees
              alone. Custom AI: $0.29 total = <strong>$0.00029/lead</strong>.
              That is 3,069x cheaper per lead.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

/**
 * Lead Scoring Breakdown Diagram
 */
export function LeadScoringDiagram() {
  const dimensions = [
    { label: 'Email provided', points: '+20', maxPoints: 20, color: '#008dcc' },
    { label: 'Name provided', points: '+10', maxPoints: 10, color: '#008dcc' },
    { label: 'Budget: $50K+', points: '+35', maxPoints: 35, color: '#22c55e' },
    {
      label: 'Subject: Project Inquiry',
      points: '+10',
      maxPoints: 10,
      color: '#8b5cf6',
    },
    {
      label: 'Message: 100+ chars',
      points: '+15',
      maxPoints: 15,
      color: '#FDC304',
    },
    {
      label: 'Urgency signals detected',
      points: '+10',
      maxPoints: 10,
      color: '#F90B8A',
    },
  ]

  const routes = [
    {
      range: '80-100',
      label: 'Priority Alert',
      description: 'Founder gets SMS-level alert within seconds',
      color: '#F90B8A',
      icon: Bell,
    },
    {
      range: '60-79',
      label: 'Qualified Lead',
      description: 'Portfolio + pricing email, notification to team',
      color: '#FDC304',
      icon: Target,
    },
    {
      range: '0-59',
      label: 'Standard Nurture',
      description: 'Warm confirmation email with service overview',
      color: '#008dcc',
      icon: Mail,
    },
  ]

  return (
    <div
      data-blog-layout='narrow'
      className='my-8 p-6 md:p-8 bg-muted/50 rounded-2xl border border-border/60'
    >
      <div className='text-center mb-6'>
        <h3 className='text-xl md:text-2xl font-bold text-foreground mb-2'>
          REAL-TIME LEAD SCORING
        </h3>
        <p className='text-sm text-muted-foreground'>
          How the qualification score is calculated (0-100)
        </p>
      </div>

      {/* Scoring Dimensions */}
      <div className='space-y-3 mb-6'>
        {dimensions.map(dim => (
          <div key={dim.label} className='flex items-center gap-3'>
            <div className='flex-1'>
              <div className='flex items-center justify-between mb-1'>
                <span className='text-xs font-medium text-foreground'>
                  {dim.label}
                </span>
                <span
                  className='text-xs font-bold'
                  style={{ color: dim.color }}
                >
                  {dim.points}
                </span>
              </div>
              <div className='w-full h-2 bg-muted rounded-full overflow-hidden'>
                <div
                  className='h-full rounded-full'
                  style={{
                    width: `${(dim.maxPoints / 100) * 100}%`,
                    backgroundColor: dim.color,
                  }}
                />
              </div>
            </div>
          </div>
        ))}
        <div className='flex items-center justify-between pt-2 border-t border-border/40'>
          <span className='text-sm font-bold text-foreground'>
            Maximum possible score
          </span>
          <span className='text-sm font-bold text-[#008dcc]'>100 points</span>
        </div>
      </div>

      {/* Routing */}
      <div className='text-center mb-4'>
        <ArrowDown className='w-5 h-5 text-muted-foreground mx-auto' />
        <span className='text-xs font-semibold text-muted-foreground uppercase tracking-wider'>
          Smart Routing
        </span>
      </div>

      <div className='space-y-2'>
        {routes.map(route => (
          <div
            key={route.range}
            className='p-3 rounded-lg bg-background border border-border/60 flex items-center gap-3'
          >
            <div
              className='w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0'
              style={{ backgroundColor: `${route.color}15` }}
            >
              <route.icon className='w-5 h-5' style={{ color: route.color }} />
            </div>
            <div className='flex-1'>
              <div className='flex items-center gap-2'>
                <span
                  className='text-xs font-bold px-2 py-0.5 rounded-full'
                  style={{
                    backgroundColor: `${route.color}15`,
                    color: route.color,
                  }}
                >
                  {route.range}
                </span>
                <span className='text-sm font-semibold text-foreground'>
                  {route.label}
                </span>
              </div>
              <p className='text-xs text-muted-foreground mt-0.5'>
                {route.description}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

/**
 * Speed to Lead Impact Diagram
 */
export function SpeedToLeadDiagram() {
  const data = [
    {
      time: 'Instant',
      multiplier: '391%',
      description: 'Our AI response',
      color: '#008dcc',
      width: '100%',
    },
    {
      time: '5 min',
      multiplier: '21x',
      description: 'Best human response',
      color: '#22c55e',
      width: '60%',
    },
    {
      time: '30 min',
      multiplier: '1x',
      description: 'Baseline qualification rate',
      color: '#FDC304',
      width: '30%',
    },
    {
      time: '1 hour',
      multiplier: '0.4x',
      description: '60% drop vs 5 min',
      color: '#f97316',
      width: '15%',
    },
    {
      time: '24 hours',
      multiplier: '0.016x',
      description: '60x less likely to qualify',
      color: '#ef4444',
      width: '5%',
    },
  ]

  return (
    <div
      data-blog-layout='narrow'
      className='my-8 p-6 md:p-8 bg-muted/50 rounded-2xl border border-border/60'
    >
      <div className='text-center mb-6'>
        <h3 className='text-xl md:text-2xl font-bold text-foreground mb-2'>
          SPEED TO LEAD: WHY SECONDS MATTER
        </h3>
        <p className='text-sm text-muted-foreground'>
          Lead qualification probability by response time (Harvard Business
          Review, InsideSales.com)
        </p>
      </div>

      <div className='space-y-3'>
        {data.map(item => (
          <div key={item.time} className='flex items-center gap-3'>
            <div className='w-16 text-right flex-shrink-0'>
              <span className='text-xs font-bold' style={{ color: item.color }}>
                {item.time}
              </span>
            </div>
            <div className='flex-1'>
              <div className='w-full h-6 bg-muted rounded-full overflow-hidden'>
                <div
                  className='h-full rounded-full flex items-center px-2'
                  style={{
                    width: item.width,
                    backgroundColor: item.color,
                    minWidth: '40px',
                  }}
                >
                  <span className='text-xs font-bold text-white whitespace-nowrap'>
                    {item.multiplier}
                  </span>
                </div>
              </div>
            </div>
            <div className='w-36 flex-shrink-0 hidden md:block'>
              <span className='text-xs text-muted-foreground'>
                {item.description}
              </span>
            </div>
          </div>
        ))}
      </div>

      <div className='mt-6 p-3 rounded-lg bg-[#008dcc]/5 border border-[#008dcc]/20'>
        <p className='text-xs text-center text-muted-foreground'>
          <strong className='text-foreground'>
            78% of buyers go with the first company that responds.
          </strong>{' '}
          The same audit found an average response time of 44 hours, while half
          of leads received no response.
        </p>
      </div>
    </div>
  )
}

/**
 * Contact Form CTA Card
 */
export function ContactFormCTA() {
  return (
    <BlogCTACard
      href='/contact-us'
      label='Try It Live'
      title='See the AI Qualification in Action'
      description='Submit our contact form and experience the instant AI response yourself. Same system described in this post, running in production right now.'
      buttonText='Try the Contact Form'
      ButtonIcon={MessageSquare}
      SideIcon={Bot}
      gradient='from-[#0077b3] via-[#0077b3] to-[#7c3aed]'
      buttonColor='text-[#0067a3]'
    />
  )
}
